import { STRING_TO_SANITIZE_NUMBER_REGEX } from '@components/bid_request/details/constants';

export type Value = number | string | null | undefined;

/**
 * Parses a numeric value from either a number or string input.
 *
 * This parsing happens AFTER Handsontable's built-in validations, so expected error states will still occur.
 *
 * @param value - The input value to parse (number, string, null, or undefined)
 * @param allowEmpty - whether to allow empty string, null, or undefined inputs to return as empty string
 * @returns - Returns an empty string if input is an empty string, otherwise ensures output is a number or 0
 *
 * Behavior:
 * - If input is an empty string (''), null, or undefined, returns empty string ('')
 * - If input is already a number, returns that number
 * - If input is a string with numeric content, extracts and returns the number
 * - If parsing fails or results in invalid number, returns 0 as fallback
 * - Always ensures non-empty return values are numeric (number type)
 *
 */
export const sanitizeNumericInput = (value: number | string | null | undefined): number | '' => {
  // Allow empty string input value for numeric custom column cell.
  if ((typeof value === 'string' && value.trim() === '') || value === null || value === undefined) {
    return '';
  }

  let number;

  if (typeof value === 'number') {
    number = value;
  } else if (typeof value === 'string') {
    const sanitizedValue = value.replace(STRING_TO_SANITIZE_NUMBER_REGEX, '').trim();

    if (!sanitizedValue.length) {
      return '';
    }
    // Remove all non-digit characters except decimal points
    // This preserves decimal numbers while removing commas, currency symbols, etc.
    number = Number(sanitizedValue);
  }

  if (Number.isNaN(number)) {
    return '';
  }

  if (!Number.isFinite(number)) {
    number = 0;
  }

  return number ?? '';
};
